import { motion, AnimatePresence } from "framer-motion";
import { XCircle, X, Loader2, AlertTriangle } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { declineDeal } from "../../requests/Uploaded_Deals/UploadedDealsService";

const DeclineDealModal = ({
  isOpen,
  onClose,
  deal,
  dealId,
  onSuccess,
  onError,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="fixed inset-0 z-[1000] flex w-full items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto w-full max-w-md min-w-0 rounded-2xl bg-white shadow-[0_25px_60px_-12px_rgba(0,0,0,0.28)] overflow-hidden box-border"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="decline-modal-title"
            >
              {/* Top accent stripe */}
              <div className="h-1 bg-gradient-to-r from-red-400 via-[#FF6C6C] to-rose-500" />

              <Formik
                initialValues={{ status: "declined", reason: "" }}
                validationSchema={Yup.object({
                  reason: Yup.string()
                    .required("Please provide a reason before submitting")
                    .min(3, "Must be at least 3 characters"),
                })}
                onSubmit={(values, actions) =>
                  declineDeal(values, dealId, {
                    ...actions,
                    onSuccess,
                    onClose,
                  })
                }
              >
                {({ isSubmitting, errors, touched }) => (
                  <Form>
                    {/* Header */}
                    <div className="px-6 pt-5 pb-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className="flex shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-red-50 border border-red-100">
                            <XCircle className="w-5 h-5 text-[#FF6C6C]" strokeWidth={2} />
                          </div>
                          <div className="min-w-0 overflow-hidden">
                            <h2
                              id="decline-modal-title"
                              className="text-lg font-bold text-gray-900 leading-tight break-words"
                            >
                              Decline Deal
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5 break-words">
                              Reject this deal submission
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={onClose}
                          disabled={isSubmitting}
                          className="p-1.5 shrink-0 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                          aria-label="Close"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Deal name card */}
                    <div className="px-6 pb-4 min-w-0 overflow-hidden">
                      <div className="flex items-start gap-3 px-4 py-3.5 bg-gray-50 rounded-xl border border-gray-200/80 min-w-0 overflow-hidden">
                        <div className="w-1 self-stretch shrink-0 rounded-full bg-[#FF6C6C]" />
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
                            Deal
                          </p>
                          <p className="text-sm font-semibold text-gray-900 break-words whitespace-normal leading-snug w-full">
                            {deal?.title ?? `#${deal?.id ?? "—"}`}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Reason textarea */}
                    <div className="px-6 pb-2">
                      <label
                        htmlFor="reason"
                        className="block text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-2"
                      >
                        Reason for declining
                      </label>
                      <Field
                        as="textarea"
                        id="reason"
                        name="reason"
                        rows={4}
                        placeholder="Describe why this deal is being declined…"
                        className={`w-full px-4 py-3 text-sm text-gray-800 placeholder-gray-400 bg-gray-50 border rounded-xl resize-none outline-none transition-all duration-150 focus:bg-white focus:ring-2 focus:border-[#FF6C6C] ${
                          errors.reason && touched.reason
                            ? "border-red-400 bg-red-50/30 focus:ring-red-200"
                            : "border-gray-200 focus:ring-[#FF6C6C]/25"
                        }`}
                      />
                      <ErrorMessage name="reason">
                        {(msg) => (
                          <motion.p
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-1.5 mt-2 text-xs text-red-500"
                          >
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            {msg}
                          </motion.p>
                        )}
                      </ErrorMessage>
                    </div>

                    {/* Warning notice */}
                    <div className="px-6 py-4 min-w-0 overflow-hidden">
                      <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 rounded-xl border border-red-100 min-w-0 overflow-hidden">
                        <AlertTriangle className="w-4 h-4 text-[#FF6C6C] shrink-0 mt-0.5" />
                        <p className="text-xs text-red-600 leading-relaxed break-words whitespace-normal min-w-0 w-full">
                          The vendor will be{" "}
                          <strong>notified</strong> of this decision along with
                          the reason you provide.
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="px-6 pb-6 flex gap-3">
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#FF6C6C] hover:bg-[#ff5252] active:bg-[#e04040] transition-all duration-150 flex items-center justify-center gap-2 shadow-sm shadow-red-200 disabled:opacity-70"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Declining…
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4" />
                            Decline Deal
                          </>
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DeclineDealModal;
